import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Youtube, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import '../styles/design-system.css';

const ContactSection = () => {
  const { contactData } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (mock)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const socialIcons = {
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube
  };

  const inputStyles = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-input)',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    width: '100%'
  };

  return (
    <section 
      id="contact" 
      className="py-20 md:py-32"
      style={{ background: 'var(--bg-page)' }}
    >
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="heading-hero mb-4">{contactData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            {contactData.subtitle}
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Contact Cards */}
            <div className="space-y-4 mb-8">
              <div className="voice-card accent-pink flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.5)' }}
                >
                  <Mail size={20} style={{ color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <p className="caption mb-1">E-mail</p>
                  <p className="body-medium">{contactData.email}</p>
                </div>
              </div>

              <div className="voice-card accent-blue flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.5)' }}
                >
                  <Phone size={20} style={{ color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <p className="caption mb-1">Telefone</p>
                  <p className="body-medium">{contactData.phone}</p>
                </div>
              </div>

              <div className="voice-card accent-green flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.5)' }}
                >
                  <MapPin size={20} style={{ color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <p className="caption mb-1">Localização</p>
                  <p className="body-medium">{contactData.address}</p>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full flex items-center justify-center gap-2 mb-8"
              style={{ background: '#25D366' }}
            >
              <MessageCircle size={18} />
              Fale pelo WhatsApp
            </a>

            {/* Social Links */}
            <div>
              <p className="body-small mb-4">Siga nas redes sociais</p>
              <div className="flex gap-3">
                {Object.entries(contactData.social).map(([platform, handle]) => {
                  const IconComponent = socialIcons[platform];
                  return (
                    <a
                      key={platform}
                      href={`https://${platform}.com/${handle.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-nav"
                      aria-label={platform}
                    >
                      <IconComponent size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="voice-card">
              <h3 className="heading-3 mb-6">Envie uma mensagem</h3>

              <div className="space-y-4">
                <div>
                  <label className="body-small block mb-2">Nome completo *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={inputStyles}
                    placeholder="Seu nome"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="body-small block mb-2">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={inputStyles}
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label className="body-small block mb-2">Telefone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      style={inputStyles}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="body-small block mb-2">Assunto *</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    style={{ ...inputStyles, cursor: 'pointer' }}
                  >
                    <option value="">Selecione um assunto</option>
                    {contactData.formFields.find(f => f.name === 'subject')?.options.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="body-small block mb-2">Mensagem *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    style={{ ...inputStyles, resize: 'vertical', minHeight: '120px' }}
                    placeholder="Escreva sua mensagem..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  style={{ opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send size={16} />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
